import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";

import { DndContext } from "@dnd-kit/core";

import type { DndContextProps, DragEndEvent, DragOverEvent, Over } from "@dnd-kit/core";

export const ACCEPTED_OVERLEAF_IMPORT_FILE_TYPES = [".zip", ".gz", ".md", ".tex"] as const;
export const ACCEPTED_OVERLEAF_IMPORT_FILE_TYPES_STRING =
  ACCEPTED_OVERLEAF_IMPORT_FILE_TYPES.join(",");
export const ACCEPTED_IMPORT_FILE_TYPES = [
  ...ACCEPTED_OVERLEAF_IMPORT_FILE_TYPES,
  ".pdf",
  ".ipynb",
] as const;
export const ACCEPTED_IMPORT_FILE_TYPES_STRING = ACCEPTED_IMPORT_FILE_TYPES.join(",");

export type DropData = {
  id: string;
};

export interface FileDragEndEvent extends DragEndEvent {
  files?: File[];
}

interface FileDndContextValue {
  isFileDragging: boolean;
  isWindowFileDragging: boolean;
  registerDroppable: (id: string, data: DropData) => void;
  unregisterDroppable: (id: string) => void;
  setFileDropOver: (id: string) => void;
  resetFileDropOver: (id: string) => void;
}

const defaultContextValue: FileDndContextValue = {
  isFileDragging: false,
  isWindowFileDragging: false,
  registerDroppable: () => {},
  unregisterDroppable: () => {},
  setFileDropOver: () => {},
  resetFileDropOver: () => {},
};

const FileDndContextInternal = createContext<FileDndContextValue>(defaultContextValue);

export function useFileDndContext() {
  return useContext(FileDndContextInternal) ?? defaultContextValue;
}

export interface FileDndContextProps extends Omit<DndContextProps, "onDragEnd"> {
  acceptedFileTypes: readonly string[];
  onDragEnd?: (event: FileDragEndEvent) => void;
  onInvalidFileDrop?: (file: File, acceptedTypes: readonly string[]) => void;
  children: React.ReactNode;
  containerRef: React.RefObject<HTMLElement | null>;
}

export function FileDndContext({
  acceptedFileTypes,
  onDragEnd,
  onDragOver,
  onInvalidFileDrop,
  children,
  containerRef,
  ...dndContextProps
}: FileDndContextProps) {
  const [isFileDragging, setIsFileDragging] = useState(false);
  const [isWindowFileDragging, setWindowIsFileDragging] = useState(false);

  const currentFileDropOverRef = useRef<string | null>(null);
  const fileDropRegistry = useRef<Map<string, DropData>>(new Map());

  const registerDroppable = useCallback((id: string, data: DropData) => {
    fileDropRegistry.current.set(id, data);
  }, []);

  const unregisterDroppable = useCallback((id: string) => {
    fileDropRegistry.current.delete(id);
  }, []);

  const setFileDropOver = useCallback((id: string) => {
    currentFileDropOverRef.current = id;
  }, []);

  const resetFileDropOver = useCallback((id: string) => {
    if (currentFileDropOverRef.current !== id) {
      return;
    }
    currentFileDropOverRef.current = null;
  }, []);

  const handleNativeDragEnter = useCallback((e: DragEvent) => {
    if (!e.dataTransfer?.types.includes("Files")) {
      return;
    }

    e.preventDefault();

    setWindowIsFileDragging(true);

    setIsFileDragging(true);
  }, []);

  const handleNativeDragOver = useCallback(
    (e: DragEvent) => {
      if (!e.dataTransfer?.types.includes("Files")) {
        return;
      }

      e.preventDefault();

      // Call the parent's onDragOver with synthetic over object
      if (onDragOver && currentFileDropOverRef.current) {
        const dropData = fileDropRegistry.current.get(currentFileDropOverRef.current);
        if (dropData) {
          onDragOver(createDragEvent<DragOverEvent>(dropData));
        }
      }
    },
    [onDragOver],
  );

  const handleNativeDragLeave = useCallback((e: DragEvent) => {
    if (
      e.relatedTarget instanceof HTMLElement &&
      e.currentTarget instanceof HTMLElement &&
      e.currentTarget.contains(e.relatedTarget)
    )
      return;

    e.preventDefault();

    setIsFileDragging(false);
    currentFileDropOverRef.current = null;
  }, []);

  const handleNativeDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setIsFileDragging(false);
      setWindowIsFileDragging(false);

      const dropTargetId = currentFileDropOverRef.current;
      currentFileDropOverRef.current = null;

      if (!dropTargetId) {
        console.warn("File dropped outside of any registered drop target");
        return;
      }

      const dropData = fileDropRegistry.current.get(dropTargetId);
      if (!dropData) {
        console.warn("Dropped on unregistered target:", dropTargetId);
        return;
      }

      const allFiles = Array.from(e.dataTransfer?.files ?? []);
      if (allFiles.length === 0) {
        return;
      }

      const validFiles: File[] = [];
      for (const file of allFiles) {
        if (acceptedFileTypes.some((type) => file.name.endsWith(type))) {
          validFiles.push(file);
        } else {
          onInvalidFileDrop?.(file, acceptedFileTypes);
        }
      }

      if (validFiles.length === 0) {
        return;
      }

      if (onDragEnd) {
        onDragEnd(createDragEvent<FileDragEndEvent>(dropData, validFiles));
      }
    },
    [acceptedFileTypes, onInvalidFileDrop, onDragEnd],
  );

  const contextValue: FileDndContextValue = useMemo(
    () => ({
      isFileDragging,
      isWindowFileDragging,
      registerDroppable,
      unregisterDroppable,
      setFileDropOver,
      resetFileDropOver,
    }),
    [
      isFileDragging,
      isWindowFileDragging,
      registerDroppable,
      unregisterDroppable,
      setFileDropOver,
      resetFileDropOver,
    ],
  );

  useEffect(() => {
    const handleWindowDragOver = (e: DragEvent) => {
      // make window an acceptable target for drop event
      e.preventDefault();
    };

    const handleWindowDragEnter = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        const overFileDropArea =
          e.target instanceof Element && !!e.target.closest("[data-file-drop-area]");
        setWindowIsFileDragging(!overFileDropArea);
      }
    };

    const handleWindowDragLeave = (e: DragEvent) => {
      if (e.relatedTarget) return;
      if (e.dataTransfer?.types?.includes("Files")) {
        setWindowIsFileDragging(false);
      }
    };

    const handleWindowDrop = (e: DragEvent) => {
      if (e.dataTransfer?.types?.includes("Files")) {
        setWindowIsFileDragging(false);
      }
    };

    window.addEventListener("dragover", handleWindowDragOver);
    window.addEventListener("dragenter", handleWindowDragEnter, true);
    window.addEventListener("dragleave", handleWindowDragLeave, true);
    window.addEventListener("drop", handleWindowDrop, true);

    return () => {
      window.removeEventListener("dragover", handleWindowDragOver);
      window.removeEventListener("dragenter", handleWindowDragEnter, true);
      window.removeEventListener("dragleave", handleWindowDragLeave, true);
      window.removeEventListener("drop", handleWindowDrop, true);
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    container.addEventListener("dragenter", handleNativeDragEnter);
    container.addEventListener("dragover", handleNativeDragOver);
    container.addEventListener("dragleave", handleNativeDragLeave);
    container.addEventListener("drop", handleNativeDrop);

    return () => {
      container.removeEventListener("dragenter", handleNativeDragEnter);
      container.removeEventListener("dragover", handleNativeDragOver);
      container.removeEventListener("dragleave", handleNativeDragLeave);
      container.removeEventListener("drop", handleNativeDrop);
    };
  }, [
    containerRef,
    handleNativeDragEnter,
    handleNativeDragOver,
    handleNativeDragLeave,
    handleNativeDrop,
  ]);

  return (
    <FileDndContextInternal.Provider value={contextValue}>
      <DndContext {...dndContextProps} onDragEnd={onDragEnd} onDragOver={onDragOver}>
        {children}
      </DndContext>
    </FileDndContextInternal.Provider>
  );
}

function createDragEvent<E extends FileDragEndEvent | DragOverEvent>(
  dropData: DropData,
  files?: File[],
): E {
  const syntheticOver: Over = {
    id: dropData.id,
    rect: {
      width: 0,
      height: 0,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    disabled: false,
    data: {
      current: dropData,
    },
  };

  const syntheticEvent = {
    active: null as unknown as E["active"],
    over: syntheticOver,
    delta: { x: 0, y: 0 },
    collisions: null,
    activatorEvent: null as unknown as Event,
    files,
  } as E;

  return syntheticEvent;
}
